import { createSystem, defaultConfig, defineConfig } from "@chakra-ui/react-v3";

const varRoot = ":host";

/**
 * NOTE: We are using the radix color system to back our semantic tokens. This means we have a scale of 12 colors, each of which should be used for specific elements on the page.
 * Read more about the radix color system here: https://www.radix-ui.com/colors/docs/palette-composition/understanding-the-scale
 */
const config = defineConfig({
  cssVarsRoot: varRoot,
  conditions: {
    light: `${varRoot} &, .light &`,
  },
  preflight: { scope: varRoot },
  globalCss: {
    [varRoot]: defaultConfig.globalCss?.html ?? {},
  },
  theme: {
    tokens: {
      colors: {
        // Radix gray scale (light mode)
        // Read more about the radix color system here: https://www.radix-ui.com/colors/docs/palette-composition/understanding-the-scale
        gray: {
          1: { value: "#fbfcfe" },
          2: { value: "#f6fafd" },
          3: { value: "#ecf1f6" },
          4: { value: "#e3e9f0" },
          5: { value: "#dbe2ea" },
          6: { value: "#d4dbe2" },
          7: { value: "#c8d0d7" },
          8: { value: "#b6bdc4" },
          9: { value: "#888f95" },
          10: { value: "#7d848b" },
          11: { value: "#5e656b" },
          12: { value: "#1b2025" },
        },
        // Radix gray scale (dark mode)
        grayDark: {
          1: { value: "#111113" },
          2: { value: "#19191b" },
          3: { value: "#222325" },
          4: { value: "#292a2e" },
          5: { value: "#303136" },
          6: { value: "#393a40" },
          7: { value: "#46484f" },
          8: { value: "#5f606a" },
          9: { value: "#6c6e79" },
          10: { value: "#797b86" },
          11: { value: "#b2b3bd" },
          12: { value: "#eeeef0" },
        },
        // Radix blue scale (light mode)
        brandRaw: {
          1: { value: "#fcfdff" },
          2: { value: "#f6f9ff" },
          3: { value: "#ecf2ff" },
          4: { value: "#dee9ff" },
          5: { value: "#cfdeff" },
          6: { value: "#bdd0ff" },
          7: { value: "#a5bcff" },
          8: { value: "#86a0ff" },
          9: { value: "#748df0" },
          10: { value: "#6981e3" },
          11: { value: "#485cba" },
          12: { value: "#1d286b" },
        },
        // Radix blue scale (dark mode)
        brandRawDark: {
          1: { value: "#0d111b" },
          2: { value: "#141724" },
          3: { value: "#1d2544" },
          4: { value: "#232d5d" },
          5: { value: "#2d396f" },
          6: { value: "#384680" },
          7: { value: "#455595" },
          8: { value: "#5265b1" },
          9: { value: "#748df0" },
          10: { value: "#6981e3" },
          11: { value: "#9ab0fd" },
          12: { value: "#d6e0fd" },
        },
        accentRaw: {
          green: {
            50: { value: "#e6f2f1" },
            100: { value: "#b3d9d6" },
            200: { value: "#80c0bb" },
            300: { value: "#4da7a0" },
            400: { value: "#498380" },
            500: { value: "#3d6f6c" },
            600: { value: "#326360" },
            700: { value: "#005452" },
            800: { value: "#004240" },
            900: { value: "#003330" },
          },
          purple: {
            50: { value: "#f3eaf5" },
            100: { value: "#ddc4e3" },
            200: { value: "#c79ed1" },
            300: { value: "#b178bf" },
            400: { value: "#9b69ad" },
            500: { value: "#975da5" },
            600: { value: "#854f92" },
            700: { value: "#73427f" },
            800: { value: "#61366c" },
            900: { value: "#4f2959" },
          },
        },
      },
    },
    semanticTokens: {
      colors: {
        // Brand colors that adapt to dark mode
        brand: {
          1: { value: { base: "{colors.brandRaw.1}", _dark: "{colors.brandRawDark.1}" } },
          2: { value: { base: "{colors.brandRaw.2}", _dark: "{colors.brandRawDark.2}" } },
          3: { value: { base: "{colors.brandRaw.3}", _dark: "{colors.brandRawDark.3}" } },
          4: { value: { base: "{colors.brandRaw.4}", _dark: "{colors.brandRawDark.4}" } },
          5: { value: { base: "{colors.brandRaw.5}", _dark: "{colors.brandRawDark.5}" } },
          6: { value: { base: "{colors.brandRaw.6}", _dark: "{colors.brandRawDark.6}" } },
          7: { value: { base: "{colors.brandRaw.7}", _dark: "{colors.brandRawDark.7}" } },
          8: { value: { base: "{colors.brandRaw.8}", _dark: "{colors.brandRawDark.8}" } },
          9: { value: { base: "{colors.brandRaw.9}", _dark: "{colors.brandRawDark.9}" } },
          10: { value: { base: "{colors.brandRaw.10}", _dark: "{colors.brandRawDark.10}" } },
          11: { value: { base: "{colors.brandRaw.11}", _dark: "{colors.brandRawDark.11}" } },
          12: { value: { base: "{colors.brandRaw.12}", _dark: "{colors.brandRawDark.12}" } },
          solid: { value: { base: "{colors.brandRaw.9}", _dark: "{colors.brandRawDark.9}" } },
          contrast: { value: "white" },
        },

        // Accent colors with dark mode support
        accent: {
          green: {
            50: {
              value: { base: "{colors.accentRaw.green.50}", _dark: "{colors.accentRaw.green.900}" },
            },
            100: {
              value: {
                base: "{colors.accentRaw.green.100}",
                _dark: "{colors.accentRaw.green.800}",
              },
            },
            200: {
              value: {
                base: "{colors.accentRaw.green.200}",
                _dark: "{colors.accentRaw.green.700}",
              },
            },
            300: {
              value: {
                base: "{colors.accentRaw.green.300}",
                _dark: "{colors.accentRaw.green.600}",
              },
            },
            400: {
              value: {
                base: "{colors.accentRaw.green.400}",
                _dark: "{colors.accentRaw.green.500}",
              },
            },
            500: {
              value: {
                base: "{colors.accentRaw.green.500}",
                _dark: "{colors.accentRaw.green.400}",
              },
            },
            600: {
              value: {
                base: "{colors.accentRaw.green.600}",
                _dark: "{colors.accentRaw.green.300}",
              },
            },
            700: {
              value: {
                base: "{colors.accentRaw.green.700}",
                _dark: "{colors.accentRaw.green.200}",
              },
            },
            800: {
              value: {
                base: "{colors.accentRaw.green.800}",
                _dark: "{colors.accentRaw.green.100}",
              },
            },
            900: {
              value: { base: "{colors.accentRaw.green.900}", _dark: "{colors.accentRaw.green.50}" },
            },
          },
          purple: {
            50: {
              value: {
                base: "{colors.accentRaw.purple.50}",
                _dark: "{colors.accentRaw.purple.900}",
              },
            },
            100: {
              value: {
                base: "{colors.accentRaw.purple.100}",
                _dark: "{colors.accentRaw.purple.800}",
              },
            },
            200: {
              value: {
                base: "{colors.accentRaw.purple.200}",
                _dark: "{colors.accentRaw.purple.700}",
              },
            },
            300: {
              value: {
                base: "{colors.accentRaw.purple.300}",
                _dark: "{colors.accentRaw.purple.600}",
              },
            },
            400: {
              value: {
                base: "{colors.accentRaw.purple.400}",
                _dark: "{colors.accentRaw.purple.500}",
              },
            },
            500: {
              value: {
                base: "{colors.accentRaw.purple.500}",
                _dark: "{colors.accentRaw.purple.400}",
              },
            },
            600: {
              value: {
                base: "{colors.accentRaw.purple.600}",
                _dark: "{colors.accentRaw.purple.300}",
              },
            },
            700: {
              value: {
                base: "{colors.accentRaw.purple.700}",
                _dark: "{colors.accentRaw.purple.200}",
              },
            },
            800: {
              value: {
                base: "{colors.accentRaw.purple.800}",
                _dark: "{colors.accentRaw.purple.100}",
              },
            },
            900: {
              value: {
                base: "{colors.accentRaw.purple.900}",
                _dark: "{colors.accentRaw.purple.50}",
              },
            },
          },
        },

        // Background colors
        bg: {
          DEFAULT: { value: { base: "{colors.gray.1}", _dark: "{colors.grayDark.1}" } },
          subtle: { value: { base: "{colors.gray.2}", _dark: "{colors.grayDark.2}" } },
          muted: { value: { base: "{colors.gray.3}", _dark: "{colors.grayDark.3}" } },
          emphasized: { value: { base: "{colors.gray.4}", _dark: "{colors.grayDark.4}" } },
          loud: { value: { base: "{colors.gray.5}", _dark: "{colors.grayDark.5}" } },
          panel: { value: { base: "{colors.gray.2}", _dark: "{colors.grayDark.2}" } },
          surface: { value: { base: "{colors.gray.1}", _dark: "{colors.grayDark.3}" } },
          canvas: { value: { base: "{colors.gray.3}", _dark: "{colors.grayDark.2}" } },
        },

        // Foreground/Text colors
        fg: {
          DEFAULT: { value: { base: "{colors.gray.12}", _dark: "{colors.grayDark.12}" } },
          muted: { value: { base: "{colors.gray.11}", _dark: "{colors.grayDark.11}" } },
          subtle: { value: { base: "{colors.gray.10}", _dark: "{colors.grayDark.10}" } },
          emphasized: { value: { base: "{colors.gray.12}", _dark: "{colors.grayDark.12}" } },
          placeholder: { value: { base: "{colors.gray.9}", _dark: "{colors.grayDark.9}" } },
        },

        // Border colors
        border: {
          DEFAULT: { value: { base: "{colors.gray.6}", _dark: "{colors.grayDark.6}" } },
          emphasized: { value: { base: "{colors.gray.7}", _dark: "{colors.grayDark.5}" } },
          subtle: { value: { base: "{colors.gray.5}", _dark: "{colors.grayDark.7}" } },
          muted: { value: { base: "{colors.gray.2}", _dark: "{colors.grayDark.10}" } },
          invisible: { value: { base: "transparent", _dark: "transparent" } },
        },

        // Interactive states
        interactive: {
          DEFAULT: { value: { base: "{colors.gray.1}", _dark: "{colors.grayDark.3}" } },
          hover: { value: { base: "{colors.gray.2}", _dark: "{colors.grayDark.4}" } },
          active: { value: { base: "{colors.gray.3}", _dark: "{colors.grayDark.5}" } },
        },

        // Focus states
        focusRing: {
          DEFAULT: { value: { base: "{colors.brand.9}", _dark: "{colors.brand.9}" } },
          shadow: {
            value: { base: "rgba(116, 141, 240, 0.6)", _dark: "rgba(116, 141, 240, 0.6)" },
          },
        },

        // Chart/Data visualization
        chart: {
          line: { value: { base: "{colors.gray.6}", _dark: "{colors.grayDark.6}" } },
          point: { value: { base: "{colors.brand.9}", _dark: "{colors.brand.9}" } },
          pointHover: { value: { base: "{colors.brand.10}", _dark: "{colors.brand.10}" } },
        },

        // Status colors (these work in both modes)
        status: {
          info: { value: { base: "{colors.brand.9}", _dark: "{colors.brand.9}" } },
          success: { value: { base: "{colors.green.500}", _dark: "{colors.green.400}" } },
          warning: { value: { base: "{colors.orange.500}", _dark: "{colors.orange.400}" } },
          error: { value: { base: "{colors.red.500}", _dark: "{colors.red.400}" } },
        },

        // Text highlight colors (for search/filter highlighting)
        highlight: {
          bg: { value: { base: "#ffeb3b", _dark: "#fbc02d" } },
          activeBg: { value: { base: "#ff9800", _dark: "#f57c00" } },
          fg: { value: { base: "#000", _dark: "#000" } },
        },

        // Button colors
        btn: {
          info: {
            bg: { value: { base: "{colors.blue.50}", _dark: "{colors.blue.900}" } },
            hoverBg: { value: { base: "{colors.blue.100}", _dark: "{colors.blue.800}" } },
            color: { value: { base: "{colors.blue.700}", _dark: "{colors.blue.200}" } },
            hoverBorder: { value: { base: "{colors.blue.400}", _dark: "{colors.blue.500}" } },
          },
          success: {
            bg: { value: { base: "{colors.green.50}", _dark: "{colors.green.900}" } },
            color: { value: { base: "{colors.green.700}", _dark: "{colors.green.200}" } },
            hoverBorder: { value: { base: "{colors.green.400}", _dark: "{colors.green.500}" } },
          },
        },
      },
    },
  },
});

export const system = createSystem(defaultConfig, config);
